/**
 * Gumloop folds everything an assistant message did before its answer into
 * one collapsible line ("16 steps · 1m 58s"). This splits a message's parts
 * the same way, in every chat Clu has (an agent's, the dock, /clu):
 *
 * - steps: every tool call, and every text part before the last tool call
 *   (Clu's narration between steps);
 * - after: the text after the last tool call (the answer), then the cards,
 *   the parts the user acts on or reads as a surface, in their order. The
 *   cards sit under the answer even when Clu wrote more after calling them,
 *   as a reloaded message has them (its text comes before its cards). A
 *   question card stays a card once answered, showing the answers chosen, so
 *   the text before it stays the answer instead of turning into narration.
 *   The last text before a card Clu writes a lead-in for (a question card, a
 *   play proposal, a connection to make, a confirmation) is the answer too,
 *   even when a read ran between them (Clu wrote what it found, read once
 *   more, then asked): it stays out of the folded steps line.
 *
 * A stretch Clu spent thinking is a row among the steps ("Thought for
 * 2.3s"), never counted in the line.
 *
 * A tool's display gives it one of three roles (see displayRole): a card the
 * user acts on (no step), a result that is both a step and a card under the
 * answer (a people table, the lists), or a step alone, with its result in the
 * step's detail line (credits, a rename, a filter). A message with no tool
 * calls has no steps.
 */
import { type IconDefinition } from '@/components/ui/app-icon';
import {
  partDisplayType,
  QUESTION_CARD_DISPLAY,
  questionCardOfPart,
  questionCardState,
  type ThreadPartLike,
} from '@/components/agent-chat/question-card.util';
import { testRunOf } from '@/components/agent-chat/agent-cards.util';
import { ONE_OFF_DISPLAYS } from '@/components/agent-chat/one-off/one-off.util';
import {
  confirmationDescription,
  SEND_SLACK_MESSAGE_ACTION,
} from '@/components/agent-chat/send-slack-message-preview.component';
import {
  LIST_SHAPING_ACTIONS,
  listShapingCardView,
} from '@/components/agent-chat/list-shaping/list-shaping.util';
import { LEARNING_DISPLAY } from '@/components/agent-chat/learning-row.util';
import { INSTANTLY_ACTIONS } from '@/components/agent-chat/clu-tool-steps';
import { isInternalList } from '@/utils/internal-list.util';
import {
  duplicatesReviewOf,
  MERGE_DUPLICATES_ACTION,
  MERGE_STARTED_DISPLAY,
} from '@/components/agent-chat/duplicate-cards.util';
import {
  canonicalToolName,
  connectionReadLabel,
  stepIcon,
  resultStepLabel,
  stepLabel,
} from '@/components/agent-chat/tool-labels';
import { asRecord } from '@/components/agent-chat/as-record.util';

/**
 * Cards the user acts on, which are no step: the question card (waiting for
 * an answer or showing the answers given), quick replies, a play proposal, a
 * connection to make, a plan to upgrade to, a cleanup merge started (its
 * card opens the review of duplicates), and a one-off's run, a live run of
 * an agent and the agent a repeat made (their cards review the run or open
 * the agent). A confirmation waiting for its answer is one too (see
 * partRole).
 */
export const INTERACTION_DISPLAYS: ReadonlySet<string> = new Set([
  QUESTION_CARD_DISPLAY,
  'quick_replies',
  // Where to start, under Clu's first answer after onboarding.
  'first_run_options',
  'play_proposal',
  'connection_required',
  // A live run that failed, told in the agent's chat: the run, and the page
  // where a reconnect or top-up is made.
  'run_diagnosis',
  // A pasted Google Sheets link: the user opens it with Google to import it.
  'google_sheet_import',
  // Rows pasted into the chat: the user reviews the import mapping from here.
  'pasted_rows_import',
  // Google Docs to pick with Google for a play (request_doc_pick).
  'google_doc_pick',
  // A linked Google Doc the user opens with Google for Clu to read it.
  'google_doc_read',
  'upgrade_prompt',
  MERGE_STARTED_DISPLAY,
  ...ONE_OFF_DISPLAYS,
]);

/**
 * Results worth seeing whole: a step like any tool, and a card under the
 * answer (people, companies, lists, a new list, leads moved, a run started).
 */
export const RESULT_DISPLAYS: ReadonlySet<string> = new Set([
  'people_table',
  'company_table',
  'company_info',
  // The company profile Clu saved from its research (onboarding's first chat).
  'company_profile',
  'lead_lists',
  'saved_agents',
  'validation_results',
  'list_created',
  'import_result',
  'leads_copied',
  'leads_moved',
  'enrichment_started',
  'smart_column_started',
  'quick_action_started',
  // A new Google Sheet or Doc, with a link to open it.
  'google_file_created',
  // Its card opens the page, and opens it again on a click.
  'navigate',
  // What Clu learned: a row with Undo, or Approve and Decline.
  LEARNING_DISPLAY,
]);

/** Every display drawn as a card after the steps line. */
export const CARD_DISPLAYS: ReadonlySet<string> = new Set([
  ...INTERACTION_DISPLAYS,
  ...RESULT_DISPLAYS,
]);

export type DisplayRole = 'card' | 'result' | 'step';

/**
 * A display's role: a card alone, a step and a card, or a step alone. Every
 * other display, a new one included, is a step: a cost estimate (Clu states
 * the cost in its answer), credits, filters applied, a rename or a delete,
 * and each of an agent's tools.
 */
export function displayRole(display: string): DisplayRole {
  if (INTERACTION_DISPLAYS.has(display)) return 'card';
  if (RESULT_DISPLAYS.has(display)) return 'result';
  return 'step';
}

/**
 * The tool name of a part that is a stretch Clu spent thinking, once it
 * ended ("Thought for 2.3s"): a row among the steps, never a step counted in
 * the line, never the answer.
 */
export const THOUGHT_PART = 'thought';

export type PartRole = 'text' | 'step' | 'result' | 'card' | 'thought' | 'skip';

export function partRole(part: ThreadPartLike): PartRole {
  if (part.type === 'text') return part.text?.trim() ? 'text' : 'skip';
  if (part.type !== 'tool-call') return 'skip';
  if (part.toolName === THOUGHT_PART) return 'thought';
  // A confirmation is a card while it waits; resolved, it is a step.
  if (part.toolName === 'confirmation_request') {
    return part.result === undefined ? 'card' : 'step';
  }
  if (part.toolName !== 'tool_result') return 'step';
  const display = partDisplayType(part) ?? 'generic';
  // Clu reading a list for itself is a step; its rows show only when the
  // user asked to see them.
  if (display === 'people_table' && isListPeek(part)) return 'step';
  const role = displayRole(display);
  // A result that failed has nothing to show: its step says why.
  if (role === 'result' && isFailedResult(part)) return 'step';
  return role;
}

/** The searches that also show people: their results are what was asked. */
const PEOPLE_SEARCHES: ReadonlySet<string> = new Set([
  'people_search',
  'web_people_search',
]);

/**
 * A list read (get_leads) Clu made for itself: anything but a people search
 * whose result carries `show_rows: false`, which the server sends unless Clu
 * set it because the user asked to see the people. A result without the key
 * is a table, so a chat saved before it, or read from a server that doesn't
 * send it yet, keeps the tables its user saw.
 */
export function isListPeek(part: ThreadPartLike): boolean {
  const name = toolNameOf(part);
  if (name && PEOPLE_SEARCHES.has(name)) return false;
  const result = part.result as { show_rows?: unknown } | undefined;
  return result?.show_rows === false;
}

export interface StepEntry {
  index: number;
  kind: 'tool' | 'narration' | 'thought';
}

export interface StepsGrouping {
  /** The tool steps and the narration between them, in order. */
  steps: StepEntry[];
  /** The number of tool calls among the steps. */
  toolCount: number;
  /** The answer's text parts, then the cards, each in their order. */
  after: number[];
}

/** A tool part's tool, by its canonical name (an alias reads as its tool). */
function toolNameOf(part: ThreadPartLike): string | undefined {
  const args = part.args as { name?: unknown } | undefined;
  return typeof args?.name === 'string'
    ? canonicalToolName(args.name)
    : undefined;
}

function isFailedResult(part: ThreadPartLike): boolean {
  const result = part.result as
    | { error?: unknown; __displayType?: unknown }
    | undefined;
  return (
    part.type === 'tool-call' &&
    part.toolName === 'tool_result' &&
    (partDisplayType(part) === 'error' ||
      (typeof result?.error === 'string' && result.error.length > 0))
  );
}

/** A run started on a list: its row under the steps is the answer. */
const RUN_DISPLAYS: ReadonlySet<string> = new Set([
  'enrichment_started',
  'smart_column_started',
]);

/** The server's stock line after a confirmed run whose result has no
 *  message (agent_chat `_ACTION_SUMMARY_BY_DISPLAY`), then and now. */
const RUN_STOCK_LINES: ReadonlySet<string> = new Set([
  'Enrichment started. Progress will update in the lead list.',
  'The smart column started. Progress will update in the lead list.',
  'Enrichment started.',
  'The smart column started.',
]);

function oneLine(text: string): string {
  return text.split(/\s+/).filter(Boolean).join(' ');
}

/**
 * The line the server writes after a confirmed run starts: the run's own
 * message ("Started Employment check on 10 people in Pilot list."), cut at
 * 320 characters with an ellipsis when longer, or its stock line. The run's
 * row already says it, so the agents look shows the row alone.
 */
function isRunAnnouncement(text: string, messages: readonly string[]): boolean {
  const line = oneLine(text);
  if (!line) return false;
  if (RUN_STOCK_LINES.has(line)) return true;
  return messages.some(
    (message) =>
      message === line ||
      (line.endsWith('…') && message.startsWith(line.slice(0, -1).trimEnd()))
  );
}

/** Clu's searches: a failed one is a filter it corrects on its next call, or
 *  a read it tries once more (the server's search guard). */
const SEARCHES: ReadonlySet<string> = new Set([
  'people_search',
  'company_search',
]);

/**
 * Each part's role in the message. A call that failed and that Clu then made
 * again successfully in the same message (a retry after a validation error)
 * is left out: it is noise, and it would turn the text before it into
 * narration. So is the server's line announcing a run whose row shows.
 * While the message runs (`running`), a failed search is left out too: Clu's
 * next call is on its way, and shown it flashed "The tool couldn't complete
 * this request" until the corrected search came back. One the turn never
 * made again shows once it ends.
 */
export function messagePartRoles(
  parts: readonly ThreadPartLike[],
  running = false
): PartRole[] {
  const roles = parts.map((part) => partRole(part));
  const runMessages: string[] = [];
  let runRow = false;
  parts.forEach((part, index) => {
    if (roles[index] !== 'result') return;
    if (!RUN_DISPLAYS.has(partDisplayType(part) ?? '')) return;
    runRow = true;
    const message = (part.result as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string') runMessages.push(oneLine(message));
  });
  if (runRow) {
    parts.forEach((part, index) => {
      if (roles[index] !== 'text') return;
      if (isRunAnnouncement(part.text ?? '', runMessages)) {
        roles[index] = 'skip';
      }
    });
  }
  parts.forEach((part, index) => {
    if (!isFailedResult(part)) return;
    const name = toolNameOf(part);
    if (!name) return;
    if (running && SEARCHES.has(name)) {
      roles[index] = 'skip';
      return;
    }
    const retried = parts
      .slice(index + 1)
      .some(
        (later) =>
          later.type === 'tool-call' &&
          toolNameOf(later) === name &&
          !isFailedResult(later)
      );
    if (retried) roles[index] = 'skip';
  });
  return roles;
}

/**
 * Cards Clu writes a lead-in line above: the question card, a play proposal
 * and a connection to make. A confirmation waiting for its answer is one too
 * (see isLeadInCard). Quick replies and result cards are not.
 */
const LEAD_IN_CARD_DISPLAYS: ReadonlySet<string> = new Set([
  QUESTION_CARD_DISPLAY,
  'play_proposal',
  'connection_required',
]);

function isLeadInCard(part: ThreadPartLike, role: PartRole): boolean {
  if (role !== 'card') return false;
  if (part.toolName === 'confirmation_request') return true;
  return LEAD_IN_CARD_DISPLAYS.has(partDisplayType(part) ?? '');
}

/**
 * The text part a card keeps out of the steps line: the last text before the
 * message's first lead-in card, when a step comes after it (otherwise it is
 * the answer already). Clu wrote its lead-in, read once more, then showed the
 * card: the line stays above the card. -1 for none.
 */
function textBeforeCard(
  parts: readonly ThreadPartLike[],
  roles: readonly PartRole[],
  lastStep: number
): number {
  const card = parts.findIndex((part, index) =>
    isLeadInCard(part, roles[index])
  );
  if (card < 0) return -1;
  const text = roles.lastIndexOf('text', card);
  return text >= 0 && text <= lastStep ? text : -1;
}

export function groupMessageParts(
  parts: readonly ThreadPartLike[],
  running = false
): StepsGrouping {
  const roles = messagePartRoles(parts, running);
  const lastStep = Math.max(
    roles.lastIndexOf('step'),
    roles.lastIndexOf('result')
  );
  const keptText = textBeforeCard(parts, roles, lastStep);
  const steps: StepEntry[] = [];
  const answer: number[] = [];
  const cards: number[] = [];
  roles.forEach((role, index) => {
    if (role === 'skip') return;
    if (role === 'thought') {
      steps.push({ index, kind: 'thought' });
      return;
    }
    if (role === 'card') {
      cards.push(index);
      return;
    }
    if (role === 'result') {
      steps.push({ index, kind: 'tool' });
      cards.push(index);
      return;
    }
    if (index <= lastStep && index !== keptText) {
      steps.push({ index, kind: role === 'step' ? 'tool' : 'narration' });
    } else {
      answer.push(index);
    }
  });
  return {
    steps,
    toolCount: steps.filter((step) => step.kind === 'tool').length,
    after: [...answer, ...cards],
  };
}

/**
 * True when a message's only steps are runs started on a list (a confirmed
 * Employment check, AI column or enrichment): each run's row says what the
 * step would, so the message is its rows, under the steps line of the
 * message that asked.
 */
export function isRunsOnly(
  parts: readonly ThreadPartLike[],
  grouping: StepsGrouping
): boolean {
  return (
    grouping.steps.length > 0 &&
    grouping.steps.every(
      (step) =>
        step.kind === 'tool' &&
        partRole(parts[step.index]) === 'result' &&
        RUN_DISPLAYS.has(partDisplayType(parts[step.index]) ?? '')
    )
  );
}

/** A tool Clu started whose result hasn't arrived (``tool_start``). */
export function isRunningPart(part: ThreadPartLike): boolean {
  return (
    part.type === 'tool-call' &&
    part.toolName === 'tool_result' &&
    part.result === undefined
  );
}

/**
 * True while the message waits on the model: nothing yet, or a finished tool
 * call last. A tool still running shows its own row instead.
 */
export function isWaitingOnModel(parts: readonly ThreadPartLike[]): boolean {
  const roles = messagePartRoles(parts);
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const role = roles[index];
    // A thought that ended leaves Clu writing next: still the model's turn.
    if (role === 'skip' || role === 'thought') continue;
    if (isRunningPart(parts[index])) return false;
    return role !== 'text';
  }
  return true;
}

/** "26s", "1m 58s", "2h 5m". */
export function formatStepsDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

/**
 * A step's time: "0.4s" and "2.3s" under ten seconds, then as the steps line
 * reads ("12s", "1m 5s").
 */
export function formatStepTime(ms: number): string {
  const safe = Math.max(0, ms);
  if (safe < 9_950) return `${(safe / 1000).toFixed(1)}s`;
  return formatStepsDuration(safe);
}

/** "Thought for 2.3s". */
export function thoughtLabel(ms: number): string {
  return `Thought for ${formatStepTime(ms)}`;
}

/** How long a thought part lasted, or null for any other part. */
export function thoughtMsOf(part: ThreadPartLike): number | null {
  if (part.type !== 'tool-call' || part.toolName !== THOUGHT_PART) return null;
  const ms = (asRecord(part.args) ?? {}).durationMs;
  return typeof ms === 'number' && Number.isFinite(ms) ? Math.max(0, ms) : null;
}

/** How long Clu spent thinking across ``parts``. */
export function thinkingTimeOf(parts: readonly ThreadPartLike[]): number {
  return parts.reduce((sum, part) => sum + (thoughtMsOf(part) ?? 0), 0);
}

/**
 * "Working · 26s" while the message runs; "16 steps · 1m 58s" after, or "16
 * steps" without timing. A message that only thought reads "Thought for 4.2s".
 */
export function stepsLineLabel({
  toolCount,
  running,
  elapsedMs,
  durationMs,
  thoughtMs = 0,
}: {
  toolCount: number;
  running: boolean;
  elapsedMs: number;
  durationMs: number | null;
  thoughtMs?: number;
}): string {
  if (running) return `Working · ${formatStepsDuration(elapsedMs)}`;
  if (toolCount === 0 && thoughtMs > 0) return thoughtLabel(thoughtMs);
  const count = `${toolCount} ${toolCount === 1 ? 'step' : 'steps'}`;
  return durationMs !== null && durationMs >= 0
    ? `${count} · ${formatStepsDuration(durationMs)}`
    : count;
}

/**
 * Whether the block is open: the user's own choice once they opened or
 * closed it, otherwise open while the message runs and closed after.
 */
export function isStepsOpen(running: boolean, manual: boolean | null): boolean {
  return manual ?? running;
}

// ---------------------------------------------------------------------------
// Detail lines: only where a step carries a result worth reading
// ---------------------------------------------------------------------------

function total(value: unknown): number | null {
  const record = asRecord(value);
  const count = record?.total;
  return typeof count === 'number' && Number.isFinite(count) ? count : null;
}

const FIELD_WORDS: Record<string, string> = {
  name: 'name',
  description: 'description',
  instructions: 'instructions',
  knowledge: 'knowledge',
  tools: 'tools',
  delivery: 'delivery',
  limits: 'limits',
  schedule: 'schedule',
  skills: 'skills',
  triggers: 'trigger',
  connections: 'connections',
  play: 'play',
  setup: 'setup',
};

/** ["instructions", "name"] as "Instructions, name". */
export function fieldsInWords(fields: unknown): string {
  if (!Array.isArray(fields)) return '';
  const words = fields
    .filter((field): field is string => typeof field === 'string')
    .map((field) => FIELD_WORDS[field] ?? field.replace(/_/g, ' '));
  const joined = words.join(', ');
  return joined ? joined.charAt(0).toUpperCase() + joined.slice(1) : '';
}

function valueLabel(value: unknown): string {
  if (typeof value === 'string') return value;
  const record = asRecord(value);
  if (!record) return '';
  for (const key of ['label', 'value', 'name']) {
    if (typeof record[key] === 'string') return record[key] as string;
  }
  return '';
}

/** A bulk write's record lists that didn't land, by what happened to them. */
const WRITE_BUCKETS: Readonly<Record<string, string>> = {
  skipped: 'Skipped',
  failed: 'Not changed',
  not_confirmed: 'Not confirmed',
};

/**
 * A connection write's lines: its message, then for a bulk one each list of
 * records that didn't land with the first names the server sent and how many
 * more. A list it has no words for reads by its own name.
 */
function connectionWriteLines(data: Record<string, unknown>): string[] {
  const lines =
    typeof data.message === 'string' && data.message ? [data.message] : [];
  const counts = asRecord(data.counts) ?? {};
  for (const [key, value] of Object.entries(data)) {
    if (key === 'done' || !Array.isArray(value)) continue;
    const names = value.flatMap((entry: unknown) => {
      const record = asRecord(entry) ?? {};
      const name = [record.name, record.id].find(
        (value) => typeof value === 'string' && value.trim()
      );
      return typeof name === 'string' ? [name.trim()] : [];
    });
    if (!names.length) continue;
    const count = numberOf(counts[key]) ?? names.length;
    const more =
      count > names.length ? ` and ${count - names.length} more` : '';
    const label =
      WRITE_BUCKETS[key] ??
      key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
    lines.push(`${label}: ${names.join(', ')}${more}`);
  }
  return lines;
}

/**
 * The detail lines under a step: the audience size, what an update changed,
 * the schedule in words, the values a lookup found, the skill created, a
 * status change, or what went wrong.
 */
export function stepDetailLines(
  toolName: string,
  result: unknown,
  display: unknown,
  { captioned = false }: { captioned?: boolean } = {}
): string[] {
  const data = asRecord(result) ?? {};
  const shown = asRecord(display) ?? {};
  const name = canonicalToolName(toolName);
  // Failed, in part or not, a write names the records it didn't change.
  if (name === 'connection_api_write') return connectionWriteLines(data);
  if (typeof data.error === 'string' && data.error) {
    const message =
      typeof data.message === 'string' && data.message
        ? data.message
        : data.error;
    return [message];
  }
  if (INSTANTLY_ACTIONS.has(name)) {
    return typeof data.message === 'string' && data.message
      ? [data.message]
      : [];
  }
  switch (name) {
    case 'size_audience': {
      const lines: string[] = [];
      const people = total(data.people);
      const companies = total(data.companies);
      // With more matching companies than the count reads, the server
      // counts people at the first ones only: a lower bound.
      const lowerBound = data.company_scope_complete === false;
      if (people !== null)
        lines.push(
          `${lowerBound ? 'At least' : 'About'} ${people.toLocaleString()} people`
        );
      if (companies !== null) {
        lines.push(
          `${companies.toLocaleString()} ${companies === 1 ? 'company' : 'companies'}`
        );
      }
      return lines;
    }
    case 'update_agent': {
      const words = fieldsInWords(shown.fields ?? data.updated);
      return words ? [words] : [];
    }
    case 'set_schedule': {
      if (typeof data.in_words === 'string' && data.in_words) {
        return [data.in_words];
      }
      const words = fieldsInWords(shown.fields ?? data.updated);
      return words ? [words] : [];
    }
    case 'lookup_filter_values': {
      const raw = Array.isArray(data.values)
        ? data.values
        : Array.isArray(data.matches)
          ? data.matches
          : [];
      const values = raw.map(valueLabel).filter(Boolean);
      return values.length ? [values.join(', ')] : [];
    }
    case 'create_skill':
    case 'create_record_skill':
    case 'update_record_skill':
    case 'load_skill': {
      const skill = asRecord(shown.skill) ?? asRecord(data.skill);
      return typeof skill?.name === 'string' && skill.name ? [skill.name] : [];
    }
    case 'propose_learning': {
      return typeof shown.label === 'string' && shown.label.trim()
        ? [shown.label.trim()]
        : [];
    }
    case 'set_agent_status':
    case 'add_crm_field_mappings':
    case 'create_crm_activity':
    case 'add_list_column':
    case 'set_column_values':
    case 'create_sequence':
    case 'edit_sequence':
    case 'set_sequence_status':
    case 'enroll_in_sequence':
    case 'send_mailbox_email':
    case 'reply_in_thread':
    case 'send_slack_message':
    case 'send_linkedin_action': {
      return typeof data.message === 'string' && data.message
        ? [data.message]
        : [];
    }
    case 'find_crm_duplicates': {
      const groups =
        typeof data.total === 'number' && Number.isFinite(data.total)
          ? data.total
          : Array.isArray(data.groups)
            ? data.groups.length
            : null;
      return groups === null
        ? []
        : [`${groups.toLocaleString()} ${groups === 1 ? 'group' : 'groups'}`];
    }
    case 'read_duplicate_group': {
      return typeof data.displayName === 'string' && data.displayName.trim()
        ? [data.displayName.trim()]
        : [];
    }
    default:
      return cluDetailLines(name, data, captioned);
  }
}

function numberOf(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(+value)) {
    return +value;
  }
  return null;
}

/** "1 list", "1,240 people". */
function counted(count: number, one: string, many: string): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

function firstText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    const first = value.find((item) => typeof item === 'string' && item.trim());
    return typeof first === 'string' ? first.trim() : '';
  }
  return '';
}

/** A people search in words: "VP Sales at Stripe in United States". */
function peopleSearchWords(params: Record<string, unknown>): string {
  const title =
    firstText(params.job_titles_include) || firstText(params.job_title);
  const company =
    firstText(params.company_name) || firstText(params.current_company_domains);
  const industry =
    firstText(params.industries_include) || firstText(params.industry);
  const place =
    firstText(params.location) ||
    firstText(params.contact_countries) ||
    firstText(params.contact_regions);
  const named = [firstText(params.first_name), firstText(params.last_name)]
    .filter(Boolean)
    .join(' ');
  const words = [
    named || title || industry,
    company ? `at ${company}` : '',
    place ? `in ${place}` : '',
  ].filter(Boolean);
  const line = words.join(' ');
  return line ? line.charAt(0).toUpperCase() + line.slice(1) : '';
}

/** A company search in words: "Fintech, 51-200 employees, in Canada". */
function companySearchWords(params: Record<string, unknown>): string {
  const words = [
    firstText(params.industries_include) ||
      firstText(params.description_keywords),
    firstText(params.company_name),
    firstText(params.headcount_ranges)
      ? `${firstText(params.headcount_ranges)} employees`
      : '',
    firstText(params.hq_countries)
      ? `in ${firstText(params.hq_countries)}`
      : '',
  ].filter(Boolean);
  return words.join(', ');
}

function listLength(value: unknown): number | null {
  return Array.isArray(value) ? value.length : null;
}

/**
 * The detail lines of Clu's own tools: what a search looked for and found,
 * how many lists or leads, the balance, the estimate, the list a step made or
 * changed, how many values were deliverable, and the note memory saved or
 * forgot. A search whose step reads its caption ("Searching people: VP
 * Sales, Fintech") shows only what it found.
 */
function cluDetailLines(
  toolName: string,
  data: Record<string, unknown>,
  captioned = false
): string[] {
  const text = (value: unknown) =>
    typeof value === 'string' && value.trim() ? value.trim() : '';
  switch (toolName) {
    case 'people_search':
    case 'show_more_results': {
      const params = asRecord(data.search_params) ?? data;
      const found = numberOf(data.total);
      return [
        captioned ? '' : peopleSearchWords(params),
        found ? counted(found, 'person', 'people') : '',
      ].filter(Boolean);
    }
    case 'company_search': {
      const params = asRecord(data.search_params) ?? data;
      const found = numberOf(data.total);
      return [
        captioned ? '' : companySearchWords(params),
        found ? counted(found, 'company', 'companies') : '',
      ].filter(Boolean);
    }
    case 'web_people_search': {
      const found = numberOf(data.total) ?? listLength(data.results);
      return [
        captioned ? '' : text(data.query),
        found !== null ? counted(found, 'person', 'people') : '',
      ].filter(Boolean);
    }
    case 'get_lead_lists': {
      const lists = Array.isArray(data.lists)
        ? data.lists.filter((list) => !isInternalList(list)).length
        : null;
      return lists !== null ? [counted(lists, 'list', 'lists')] : [];
    }
    case 'get_leads': {
      const name = text(data.list_name);
      return name ? [name] : [];
    }
    case 'list_saved_agents': {
      const agents = listLength(data.agents);
      return agents !== null ? [counted(agents, 'agent', 'agents')] : [];
    }
    case 'get_credit_balance': {
      const credits = numberOf(data.credits_available);
      return credits !== null
        ? [
            `${credits.toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })} credits`,
          ]
        : [];
    }
    case 'estimate_cost': {
      const cost = numberOf(data.total_cost);
      return cost !== null
        ? [
            `About ${cost.toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })} credits`,
          ]
        : [];
    }
    case 'create_lead_list':
    case 'rename_lead_list':
    case 'delete_lead_list': {
      const name = text(data.name) || text(data.list_name);
      return name ? [name] : [];
    }
    case 'filter_list':
    case 'apply_filters': {
      const filters =
        numberOf(data.filter_count) ?? listLength(data.filters) ?? 0;
      return [filters > 0 ? counted(filters, 'filter', 'filters') : 'Cleared'];
    }
    case 'import_to_list': {
      const imported = numberOf(data.imported);
      return imported !== null ? [counted(imported, 'lead', 'leads')] : [];
    }
    case 'copy_leads':
    case 'move_leads':
    case 'delete_leads': {
      const moved =
        numberOf(data.copied_count) ??
        numberOf(data.moved_count) ??
        numberOf(data.deleted_count);
      const skipped = numberOf(data.skipped_duplicates) ?? 0;
      return [
        moved !== null ? counted(moved, 'lead', 'leads') : '',
        skipped > 0
          ? `${counted(skipped, 'duplicate', 'duplicates')} skipped`
          : '',
      ].filter(Boolean);
    }
    case 'validate_contacts': {
      const valid = numberOf(data.valid_count);
      const all = numberOf(data.total) ?? listLength(data.results);
      // A phone check is valid or not; deliverable is an email's word.
      const word = data.kind === 'phone' ? 'valid' : 'deliverable';
      return valid !== null && all
        ? [`${valid.toLocaleString()} of ${all.toLocaleString()} ${word}`]
        : [];
    }
    case 'navigate_to': {
      const path = text(data.path);
      return path ? [pageInWords(path)] : [];
    }
    case 'change_clu_memory': {
      const key = text(data.key);
      if (!key) return [];
      return [data.forgotten === true ? `Forgot ${key}` : `Saved ${key}`];
    }
    default:
      return [];
  }
}

/** A page Clu opened, in words: "People search", "Plans", "A list". */
export function pageInWords(path: string): string {
  if (path === '__chatwoot') return 'Live chat';
  if (path.startsWith('/lists/')) return 'A list';
  if (path.startsWith('/finder') || path.startsWith('/search')) {
    return path.includes('tab=compan') ? 'Company search' : 'People search';
  }
  const page = path.replace(/^\//, '').split(/[/?#]/)[0].replace(/[-_]/g, ' ');
  return page ? page.charAt(0).toUpperCase() + page.slice(1) : 'Home';
}

/** What a tool step shows: its label, icon and detail lines. */
export interface ToolStepView {
  label: string;
  icon: IconDefinition;
  details: string[];
  /** How long the call ran, once finished (live messages only). */
  durationMs?: number;
  /** When a call still running started (ms since epoch), for its time. */
  startedAt?: number;
  /** A test run the step started, shown as "Open run" under it. */
  run?: { runId: string; agentId: string | undefined };
  /**
   * The review of duplicates a cleanup read was about, shown as "Review
   * duplicates" under it, with the scan it read highlighted.
   */
  review?: { agentId: string | undefined; runId: string | undefined };
  /** The list a read was about, shown as "Open list" under it. */
  list?: { listId: string };
}

/** A confirmation's description without its markdown bold. */
function confirmationText(description: unknown): string {
  return typeof description === 'string'
    ? description.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim()
    : '';
}

/** The caption the server sent for a part's call, or undefined. */
function captionOfPart(args: Record<string, unknown>): string | undefined {
  return typeof args.caption === 'string' && args.caption.trim()
    ? args.caption.trim()
    : undefined;
}

/**
 * A converted tool part as a step. A call the server captioned reads its
 * caption ("Searching people: VP Sales, Fintech") while it runs and once
 * done, in place of the tool's fixed label, and a finished call carries how
 * long it ran. An answered (or passed over) question card reads "Asking for
 * your input" with a Question: answer line per question.
 */
export function toolStepView(
  part: ThreadPartLike,
  context: { followingText: string | null; isLastQuestionCard: boolean }
): ToolStepView {
  const args = asRecord(part.args) ?? {};
  const result = asRecord(part.result) ?? {};
  if (part.toolName === 'confirmation_request') {
    // A list-shaping card reads as what it did and what it could cost, in
    // full, not its description cut inside a filter's values.
    const shaping = LIST_SHAPING_ACTIONS.has(String(args.tool_name))
      ? listShapingCardView(
          args as { tool_name?: string } & Record<string, unknown>
        )
      : null;
    const description =
      shaping?.summary ??
      confirmationText(
        confirmationDescription(
          args as { tool_name?: string; description?: string }
        )
      );
    return {
      label: stepLabel('request_agent_status'),
      icon: stepIcon(
        args.tool_name === 'set_agent_status'
          ? 'request_agent_status'
          : args.tool_name === MERGE_DUPLICATES_ACTION
            ? 'request_merge_duplicate_groups'
            : args.tool_name === 'add_crm_field_mappings'
              ? 'request_crm_field_mappings'
              : args.tool_name === 'create_crm_activity'
                ? 'request_crm_activity'
                : args.tool_name === 'connection_api_write'
                  ? 'request_connection_api_write'
                  : args.tool_name === SEND_SLACK_MESSAGE_ACTION
                    ? 'request_send_slack_message'
                    : LIST_SHAPING_ACTIONS.has(String(args.tool_name)) ||
                        INSTANTLY_ACTIONS.has(String(args.tool_name))
                      ? `request_${String(args.tool_name)}`
                      : 'confirmation'
      ),
      details: description ? [description] : [],
    };
  }
  const toolName =
    typeof args.name === 'string'
      ? args.name
      : typeof result.__toolName === 'string'
        ? result.__toolName
        : (part.toolName ?? '');
  const inputArgs = asRecord(args.inputArgs) ?? {};
  // A connection read named from its own args wins over the server's caption.
  const caption =
    canonicalToolName(toolName) === 'read_connection_api' &&
    connectionReadLabel(inputArgs, true)
      ? undefined
      : captionOfPart(args);
  if (isRunningPart(part)) {
    return {
      label: caption ?? stepLabel(toolName, true, inputArgs),
      icon: stepIcon(toolName),
      details: [],
      ...(typeof args.startedAt === 'number' && Number.isFinite(args.startedAt)
        ? { startedAt: args.startedAt }
        : {}),
    };
  }
  const durationMs =
    typeof args.durationMs === 'number' && Number.isFinite(args.durationMs)
      ? Math.max(0, args.durationMs)
      : undefined;
  const display = result.__display;
  const displayType = asRecord(display)?.type;
  const isError =
    displayType === 'error' ||
    (typeof result.error === 'string' && result.error.length > 0);
  const card = questionCardOfPart(part);
  if (card) {
    const state = questionCardState(
      card,
      context.followingText,
      context.isLastQuestionCard
    );
    return {
      label: stepLabel('ask_user'),
      icon: stepIcon('ask_user'),
      details: state.kind === 'open' ? [] : state.lines,
    };
  }
  const run =
    displayType === 'agent_test_started'
      ? (testRunOf(display, result) ?? undefined)
      : undefined;
  const review = isError
    ? undefined
    : (duplicatesReviewOf(toolName, result) ?? undefined);
  // A label the result changes ("No one added to the sequence") says more
  // than the caption of what was asked.
  const fixed = stepLabel(toolName, false, inputArgs);
  const fromResult = resultStepLabel(toolName, result, inputArgs);
  const listId =
    !isError &&
    canonicalToolName(toolName) === 'get_leads' &&
    typeof result.list_id === 'string' &&
    result.list_id
      ? result.list_id
      : undefined;
  return {
    label: fromResult !== fixed ? fromResult : (caption ?? fixed),
    icon: stepIcon(toolName, isError),
    details: stepDetailLines(toolName, result, display, {
      captioned: caption !== undefined,
    }),
    ...(durationMs !== undefined ? { durationMs } : {}),
    ...(run ? { run } : {}),
    ...(review ? { review } : {}),
    ...(listId ? { list: { listId } } : {}),
  };
}

/** Text grows from a fixed tool boundary; tool identities survive index shifts. */
export function messagePartKey(
  parts: readonly ThreadPartLike[],
  index: number
): string {
  const part = parts[index];
  if (part.toolCallId) return `tool:${part.toolCallId}`;
  if (part.type === 'text') {
    for (let before = index - 1; before >= 0; before -= 1) {
      const previous = parts[before];
      if (previous.toolCallId) return `text:${previous.toolCallId}`;
    }
    return 'text:start';
  }
  return `${part.type}:${index}`;
}
